import { Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { type AdminHouseholdSummary, useUnarchiveHousehold } from "@/lib/api";
import { relativeTime } from "@/lib/relative-time";
import { cn } from "@/lib/utils";

import { ArchiveHouseholdDialog } from "./archive-household-dialog";
import { displayStatus, toneClass } from "./status";

interface HouseholdTableProps {
  households: AdminHouseholdSummary[];
}

export function HouseholdTable({ households }: HouseholdTableProps) {
  const navigate = useNavigate();
  const unarchiveHousehold = useUnarchiveHousehold();
  const [archiveTarget, setArchiveTarget] = useState<AdminHouseholdSummary | null>(null);

  if (households.length === 0) {
    return (
      <div className="bg-card shadow-elevation rounded-md py-12 text-center">
        <p className="text-muted text-[15px]">No households found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="shadow-elevation bg-card overflow-hidden rounded-md">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-border border-b">
                <th className="text-muted px-5 py-3 text-left text-[13px] font-semibold">Name</th>
                <th className="text-muted px-5 py-3 text-left text-[13px] font-semibold">
                  Contact email
                </th>
                <th className="text-muted px-5 py-3 text-left text-[13px] font-semibold">Status</th>
                <th className="text-muted px-5 py-3 text-left text-[13px] font-semibold">
                  Progress
                </th>
                <th className="text-muted px-5 py-3 text-left text-[13px] font-semibold">
                  Handoff
                </th>
                <th className="px-5 py-3 text-right text-[13px] font-semibold">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {households.map((household) => {
                const shown = displayStatus(
                  household.status,
                  household.completed_steps,
                  household.total_steps,
                );
                return (
                  <tr
                    key={household.id}
                    onClick={() => void navigate(`/admin/households/${household.id}`)}
                    className="border-border hover:bg-background cursor-pointer border-b transition-colors last:border-0"
                  >
                    <td className="text-primary px-5 py-4 text-[15px] font-medium">
                      {household.name}
                    </td>
                    <td className="text-muted px-5 py-4 text-[15px]">{household.contact_email}</td>
                    <td className="px-5 py-4">
                      <span
                        className={cn(
                          "inline-flex rounded-sm px-2.5 py-1 text-[13px] font-semibold whitespace-nowrap",
                          toneClass(shown.tone),
                        )}
                      >
                        {shown.label}
                      </span>
                    </td>
                    <td className="text-muted px-5 py-4 text-[15px] whitespace-nowrap">
                      {household.completed_steps > 0 && (
                        <>
                          <span className="text-primary tabular-nums">
                            {household.completed_steps} of {household.total_steps}
                          </span>
                          {(household.skipped_steps ?? 0) > 0 && (
                            <>, {household.skipped_steps} skipped</>
                          )}
                          {household.last_step_at && <> · {relativeTime(household.last_step_at)}</>}
                        </>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="bg-surface-subtle h-2 w-20 overflow-hidden rounded-sm">
                          <div
                            className={cn(
                              "h-full",
                              household.readiness_percent === 100 ? "bg-success" : "bg-accent",
                            )}
                            style={{ width: `${household.readiness_percent}%` }}
                          />
                        </div>
                        <span className="text-primary text-[12.5px] tabular-nums">
                          {household.readiness_percent}%
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      {household.archived_at != null ? (
                        <button
                          type="button"
                          aria-label="Restore household"
                          title="Restore"
                          disabled={
                            unarchiveHousehold.isPending &&
                            unarchiveHousehold.variables?.id === household.id
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            unarchiveHousehold.mutate({ id: household.id });
                          }}
                          className="text-muted hover:text-primary hover:bg-background focus-visible:outline-ring inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <ArrowCounterClockwise className="h-4 w-4" weight="light" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label="Archive household"
                          title="Archive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setArchiveTarget(household);
                          }}
                          className="text-muted hover:text-primary hover:bg-background focus-visible:outline-ring inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Archive className="h-4 w-4" weight="light" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ArchiveHouseholdDialog
        household={archiveTarget}
        open={archiveTarget != null}
        onOpenChange={(open) => !open && setArchiveTarget(null)}
      />
    </>
  );
}
